/**
 * useListings hook
 *
 * Provides listing search with debounced filtering, pagination,
 * and URL state synchronization.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { searchListings, loadMoreListings } from "../lib/api/listingsApi";
import {
  LISTINGS_QUERY_CONFIG,
  BOOLEAN_FILTER_KEYS,
} from "../lib/transformers/listings";
import { buildQueryString, parseQueryString } from "../lib/utils/queryString";
import type {
  Listing,
  ListingsSearchParams,
  ListingsSearchResponse,
} from "../types/listings";

// ============================================
// Constants
// ============================================

/** Debounce delay in milliseconds for filter changes */
const DEBOUNCE_DELAY = 300;

/** Default page size for listings */
const DEFAULT_LIMIT = 20;

// ============================================
// Types
// ============================================

export interface UseListingsOptions {
  /** Initial filters from URL or props */
  initialFilters?: ListingsSearchParams;
  /** Whether to sync filters to URL (default: true) */
  syncToUrl?: boolean;
  /** Debounce delay override */
  debounceDelay?: number;
  /** Auto-fetch on mount (default: true) */
  autoFetch?: boolean;
}

export interface UseListingsResult {
  /** Current listings */
  listings: Listing[];
  /** Loading state for initial/filter fetch */
  isLoading: boolean;
  /** Loading state for pagination */
  isLoadingMore: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Total count of matching listings */
  totalCount: number;
  /** Whether more results are available */
  hasMore: boolean;
  /** Current search parameters */
  searchParams: ListingsSearchParams;
  /** Update search params (debounced) */
  updateFilters: (updates: Partial<ListingsSearchParams>) => void;
  /** Reset all filters to defaults */
  resetFilters: () => void;
  /** Load next page of results */
  loadMore: () => Promise<void>;
  /** Manually trigger a refresh */
  refresh: () => Promise<void>;
}

// ============================================
// URL Parsing Utilities
// ============================================

/**
 * Parse URL search params to ListingsSearchParams
 * Uses shared config for consistent parsing
 *
 * @param urlParams - Browser URLSearchParams
 * @returns Typed search parameters
 */
function parseUrlParams(urlParams: URLSearchParams): ListingsSearchParams {
  // Use shared parser for most params
  const parsed = parseQueryString<Record<string, unknown>>(
    urlParams,
    LISTINGS_QUERY_CONFIG,
  ) as ListingsSearchParams;

  // Handle boolean filters explicitly (they need special parsing)
  for (const key of BOOLEAN_FILTER_KEYS) {
    const value = urlParams.get(key);
    if (value === "true") {
      (parsed as Record<string, boolean>)[key] = true;
    }
  }

  return parsed;
}

/**
 * Convert ListingsSearchParams to URLSearchParams
 * Uses shared config for consistent serialization
 *
 * @param params - Search parameters
 * @returns URLSearchParams for browser history
 */
function paramsToUrl(params: ListingsSearchParams): URLSearchParams {
  const queryString = buildQueryString(
    params as Record<string, unknown>,
    LISTINGS_QUERY_CONFIG,
  );
  return new URLSearchParams(queryString);
}

// ============================================
// Hook Implementation
// ============================================

/**
 * Hook for managing listings search with URL sync and pagination
 *
 * @param options - Configuration options
 * @returns Listings state and control functions
 *
 * @example
 * ```tsx
 * function ListingsPage() {
 *   const {
 *     listings,
 *     isLoading,
 *     updateFilters,
 *     loadMore,
 *     hasMore
 *   } = useListings({ syncToUrl: true });
 *
 *   return (
 *     <div>
 *       <FilterPanel onChange={updateFilters} />
 *       <ListingGrid listings={listings} loading={isLoading} />
 *       {hasMore && <LoadMoreButton onClick={loadMore} />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useListings(
  options: UseListingsOptions = {},
): UseListingsResult {
  const {
    initialFilters = {},
    syncToUrl = true,
    debounceDelay = DEBOUNCE_DELAY,
    autoFetch = true,
  } = options;

  // URL search params for syncing
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  // Initialize search params from URL or props - computed once
  const initialParams = useMemo(() => {
    // Prefer initialFilters if provided (they already contain URL params merged)
    // Otherwise parse from URL for backward compatibility
    if (Object.keys(initialFilters).length > 0) {
      return initialFilters;
    }
    if (syncToUrl && urlSearchParams.toString()) {
      return parseUrlParams(urlSearchParams);
    }
    return {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only compute once on mount

  // State
  const [searchParams, setSearchParams] =
    useState<ListingsSearchParams>(initialParams);
  const [listings, setListings] = useState<Listing[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);

  // Refs for debouncing and cleanup
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const initialFetchDoneRef = useRef(false);

  /**
   * Fetch listings from API
   *
   * @param params - Search parameters
   * @param append - If true, append to existing listings (pagination)
   */
  const fetchListings = useCallback(
    async (params: ListingsSearchParams, append = false) => {
      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        setError(null);
      }

      try {
        let result: ListingsSearchResponse;

        if (append && nextCursor) {
          result = await loadMoreListings(params, nextCursor);
        } else {
          result = await searchListings({
            ...params,
            limit: params.limit || DEFAULT_LIMIT,
          });
        }

        if (append) {
          setListings((prev) => [...prev, ...result.listings]);
        } else {
          setListings(result.listings);
        }

        setTotalCount(result.totalCount);
        setHasMore(result.hasMore);
        setNextCursor(result.nextCursor);
      } catch (err) {
        // Ignore aborted requests
        if (err instanceof Error && err.name === "AbortError") {
          return;
        }
        setError(
          err instanceof Error ? err.message : "Failed to load listings",
        );
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [nextCursor],
  );

  /**
   * Debounced search to avoid excessive API calls
   */
  const debouncedSearch = useCallback(
    (params: ListingsSearchParams) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        fetchListings(params);
      }, debounceDelay);
    },
    [fetchListings, debounceDelay],
  );

  /**
   * Update filter parameters
   * Syncs to URL and triggers debounced search
   */
  const updateFilters = useCallback(
    (updates: Partial<ListingsSearchParams>) => {
      setSearchParams((prev) => {
        const newParams = { ...prev, ...updates };

        // Sync to URL if enabled
        if (syncToUrl) {
          setUrlSearchParams(paramsToUrl(newParams), { replace: true });
        }

        // Trigger debounced search
        debouncedSearch(newParams);

        return newParams;
      });
    },
    [syncToUrl, setUrlSearchParams, debouncedSearch],
  );

  /**
   * Reset all filters to defaults
   */
  const resetFilters = useCallback(() => {
    const defaultParams: ListingsSearchParams = {};
    setSearchParams(defaultParams);

    if (syncToUrl) {
      setUrlSearchParams(new URLSearchParams(), { replace: true });
    }

    fetchListings(defaultParams);
  }, [syncToUrl, setUrlSearchParams, fetchListings]);

  /**
   * Load next page of results
   */
  const loadMore = useCallback(async () => {
    if (!hasMore || isLoadingMore) return;
    await fetchListings(searchParams, true);
  }, [hasMore, isLoadingMore, fetchListings, searchParams]);

  /**
   * Manually refresh current results
   */
  const refresh = useCallback(async () => {
    await fetchListings(searchParams);
  }, [fetchListings, searchParams]);

  // Auto-fetch on mount with initial params
  useEffect(() => {
    if (autoFetch && !initialFetchDoneRef.current) {
      initialFetchDoneRef.current = true;
      // Use initialParams directly to avoid stale closure
      fetchListings(initialParams);
    }

    // Cleanup on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFetch, fetchListings]);

  return {
    listings,
    isLoading,
    isLoadingMore,
    error,
    totalCount,
    hasMore,
    searchParams,
    updateFilters,
    resetFilters,
    loadMore,
    refresh,
  };
}

export default useListings;
